import { useId, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import sharedStyles from '@/pages/app/Enterprises/components/styles.module.scss'

import { isApiError } from '@/api'
import type { EnterpriseMember } from '@/api/generated'
import { updateNotetakerEntitlements } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import Select from '@/components/Select'
import { useToast } from '@/components/Toast/ToastContext'
import { enterpriseDetailsQueryKey } from '@/hooks/useEnterpriseDetails'

const options = [
  { value: 'allow', label: 'Allow' },
  { value: 'block', label: 'Block' },
  { value: 'existing', label: 'Use existing rules' },
]
const schema = z.object({ override: z.enum(['allow', 'block', 'existing']) })
type FormValues = z.infer<typeof schema>
export const overrideLabel = (value: boolean | null | undefined) =>
  value === undefined ? 'Unavailable' : value === null ? 'Use existing rules' : value ? 'Allow' : 'Block'

interface Props {
  enterpriseId: string
  enterpriseName: string
  members: EnterpriseMember[]
  isDisabled: boolean
  close: () => void
  onSaved: () => void
}

const NotetakerOverrideDialog = ({ enterpriseId, enterpriseName, members, isDisabled, close, onSaved }: Props) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const selectId = useId()
  const inFlight = useRef(false)
  const first = members[0].notetakerEntitlement
  const same = members.every((member) => member.notetakerEntitlement === first)
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues:
      same && first !== undefined ? { override: first === null ? 'existing' : first ? 'allow' : 'block' } : {},
  })
  const mutation = useMutation({
    mutationFn: (entitled: boolean | null) =>
      updateNotetakerEntitlements(
        enterpriseId,
        members.map((m) => m.userId),
        entitled
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['enterprise-members', enterpriseId] }),
        queryClient.invalidateQueries({ queryKey: enterpriseDetailsQueryKey(enterpriseId) }),
      ])
      updateToast('Notetaker overrides saved', 'success')
      onSaved()
    },
    onSettled: () => {
      inFlight.current = false
    },
  })
  const pending = mutation.isPending
  const save = handleSubmit(({ override }) => {
    if (isDisabled || inFlight.current || members.some((member) => member.notetakerEntitlement === undefined)) return
    inFlight.current = true
    mutation.mutate(override === 'existing' ? null : override === 'allow')
  })
  const tryClose = () => {
    if (!inFlight.current) close()
  }
  const error = mutation.error

  return (
    <DialogWrapper
      isOpen
      title="Edit Notetaker override"
      subtitle={`${members.length} selected in ${enterpriseName}`}
      close={tryClose}
    >
      <form className={sharedStyles.dialogContent} onSubmit={save}>
        {members.length === 1 && (
          <p className={sharedStyles.dialogDetailItem}>{members[0].email ?? members[0].userId}</p>
        )}
        <p className={sharedStyles.dialogDetailItem}>
          Allow bypasses rollout and the organization’s Notetaker setting. Privacy, HIPAA, and other access restrictions
          still apply.
        </p>
        <p className={sharedStyles.dialogDetailItem}>
          Block prevents Notetaker use. Use existing rules clears the override. Overrides have no expiry and are cleared
          when an account joins or leaves an organization.
        </p>
        <label htmlFor={selectId}>Notetaker override</label>
        <Controller
          name="override"
          control={control}
          render={({ field }) => (
            <Select
              id={selectId}
              value={field.value ?? ''}
              options={options}
              placeholder="Choose an override"
              onChange={field.onChange}
              isDisabled={isDisabled || pending}
            />
          )}
        />
        {error && (
          <p role="alert" className={sharedStyles.dialogWarningMessage}>
            {isApiError(error) ? error.detail || error.message : "Couldn't save the overrides. Try again."}
          </p>
        )}
        <div className={sharedStyles.dialogActions}>
          <Button title="Cancel" onClick={tryClose} type="light" isDisabled={pending} />
          <Button
            title="Save"
            onClick={() => {
              void save()
            }}
            type="dark"
            isLoading={pending}
            isDisabled={!isValid || isDisabled || pending}
          />
        </div>
      </form>
    </DialogWrapper>
  )
}

export default NotetakerOverrideDialog
